import React, { useState, useEffect, useCallback } from "react";
import {
  UserGroupIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon,
  BellAlertIcon,
  ArrowPathIcon,
  UserIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  SignalIcon,
  AdjustmentsHorizontalIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowsUpDownIcon,
} from "@heroicons/react/24/outline";
import { metricsService } from "../services/api";
import { getConversationUrl } from "../config/chatwoot";
import FilterBar from "../components/FilterBar";
import PendingConversationsTable from "../components/PendingConversationsTable";
import AgentProductivityCards from "../components/AgentProductivityCards";

// Componente Tooltip para explicações
const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block ml-1">
      <InformationCircleIcon
        className="w-4 h-4 text-gray-400 hover:text-primary-600 cursor-help transition-colors"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      />
      {showTooltip && (
        <div className="absolute z-50 w-64 p-3 text-sm text-white bg-gray-900 rounded-lg shadow-lg -left-32 top-6 animate-fadeIn">
          <div className="relative">
            {text}
            <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -top-4 left-1/2"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Função para formatar minutos de forma amigável
const formatMinutes = (minutes) => {
  // Garante que não seja negativo ou inválido
  if (minutes === null || minutes === undefined || isNaN(minutes)) return "-";

  // Garantir valor positivo
  const absMinutes = Math.abs(Math.round(minutes));

  if (absMinutes === 0) return "Agora";

  // Menos de 1 hora: apenas minutos
  if (absMinutes < 60) return `${absMinutes}min`;

  const totalHours = Math.floor(absMinutes / 60);
  const mins = absMinutes % 60;

  // Menos de 1 dia: formato hh:mm
  if (totalHours < 24) {
    return `${String(totalHours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  }

  // 1 dia ou mais: formato "Xd hh:mm"
  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  return `${days}d ${String(remainingHours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const SupervisorDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [supervisorData, setSupervisorData] = useState(null);
  const [realTimeKPIs, setRealTimeKPIs] = useState(null);
  const [pendingConversations, setPendingConversations] = useState(null);
  const [agentProductivity, setAgentProductivity] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [slaMinutes, setSlaMinutes] = useState(5);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => ({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date().toISOString(),
  }));
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingSortField, setPendingSortField] = useState('waiting_time_minutes');
  const [pendingSortDirection, setPendingSortDirection] = useState('desc');
  const [expandedSlaAgents, setExpandedSlaAgents] = useState({});


  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        slaMinutes,
        queueThreshold: 10,
        ...filters,
      };

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const pendingParams = {
        ...params,
        page: pendingPage,
        limit: 20,
        sortField: pendingSortField,
        sortDirection: pendingSortDirection,
      };

      const productivityParams = {
        ...params,
        pendingThreshold: 5,
      };

      const [
        supervisorRes,
        realTimeRes,
        pendingRes,
        productivityRes,
      ] = await Promise.all([
        metricsService.getSupervisorDashboard(params),
        metricsService.getRealTimeKPIs(params),
        metricsService.getPendingConversations(pendingParams),
        metricsService.getAgentProductivity(productivityParams),
      ]);

      setSupervisorData(supervisorRes.data.data);
      setRealTimeKPIs(realTimeRes.data.data);
      setPendingConversations(pendingRes.data.data);
      setAgentProductivity(productivityRes.data.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error loading supervisor dashboard:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [slaMinutes, filters, pendingPage, pendingSortField, pendingSortDirection]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto refresh a cada 30 segundos
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPendingPage(1); // Reset page when filters change
  };

  const handlePageChange = (newPage) => {
    setPendingPage(newPage);
  };

  const handleSortChange = (field, direction) => {
    setPendingSortField(field);
    setPendingSortDirection(direction);
    setPendingPage(1);
  };

  if (loading && !supervisorData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando dashboard do supervisor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md shadow-lg">
          <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar dashboard</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const summary = supervisorData?.summary || {};
  const onlineStatus = supervisorData?.online_status || {};
  const queueStatus = supervisorData?.queue_status || {};
  const slaStatus = supervisorData?.sla_status || {};
  const performanceStatus = supervisorData?.performance_status || {};
  const actionAlerts = supervisorData?.action_alerts || {};

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Dashboard do Supervisor</h1>
            <p className="text-gray-500 text-sm">
              Atualizado: {lastUpdate.toLocaleString("pt-BR")}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">SLA (min):</label>
              <input
                type="number"
                value={slaMinutes}
                onChange={(e) => setSlaMinutes(parseInt(e.target.value) || 5)}
                className="px-3 py-1 bg-gray-50 border border-gray-300 rounded text-gray-800 text-sm w-16 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded text-primary-500 focus:ring-primary-500"
              />
              Auto-refresh (30s)
            </label>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded flex items-center gap-2 transition-colors ${
                showFilters ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              Filtros
            </button>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 flex items-center gap-2 transition-colors"
              disabled={loading}
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* FilterBar (condicional) */}
        {showFilters && (
          <FilterBar
            onFilterChange={handleFilterChange}
            showConversationType={true}
            showTeams={true}
            showLabels={true}
          />
        )}

        {/* Resumo Executivo */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-sm border-l-4 border-green-500 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <SignalIcon className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-green-700 text-sm font-medium inline-flex items-center">
                Online Agora
                <InfoTooltip text="Número de agentes atualmente online e disponíveis para atender. Status = 'Online' (availability = 0)" />
              </span>
            </div>
            <p className="text-3xl font-bold text-green-600">{summary.agents_online || 0}</p>
          </div>
          <div className={`bg-white rounded-xl shadow-sm border-l-4 ${queueStatus.agents_with_high_queue > 0 ? 'border-orange-500' : 'border-gray-300'} p-4 hover:shadow-md transition-shadow`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 ${queueStatus.agents_with_high_queue > 0 ? 'bg-orange-100' : 'bg-gray-100'} rounded-lg`}>
                <UserGroupIcon className={`w-5 h-5 ${queueStatus.agents_with_high_queue > 0 ? 'text-orange-600' : 'text-gray-500'}`} />
              </div>
              <span className={`text-sm font-medium ${queueStatus.agents_with_high_queue > 0 ? 'text-orange-700' : 'text-gray-600'} inline-flex items-center`}>
                Fila Alta
                <InfoTooltip text="Número de AGENTES que têm muitas conversas em sua fila. Fila crítica = 10 ou mais conversas abertas/pendentes por agente (configurável via queueThreshold)" />
              </span>
            </div>
            <p className={`text-3xl font-bold ${queueStatus.agents_with_high_queue > 0 ? 'text-orange-600' : 'text-gray-500'}`}>{summary.agents_with_high_queue || 0}</p>
          </div>
          <div className={`bg-white rounded-xl shadow-sm border-l-4 ${slaStatus.agents_breaching_sla > 0 ? 'border-red-500' : 'border-gray-300'} p-4 hover:shadow-md transition-shadow`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 ${slaStatus.agents_breaching_sla > 0 ? 'bg-red-100' : 'bg-gray-100'} rounded-lg`}>
                <ExclamationTriangleIcon className={`w-5 h-5 ${slaStatus.agents_breaching_sla > 0 ? 'text-red-600' : 'text-gray-500'}`} />
              </div>
              <span className={`text-sm font-medium ${slaStatus.agents_breaching_sla > 0 ? 'text-red-700' : 'text-gray-600'} inline-flex items-center`}>
                Estourando SLA
                <InfoTooltip text={`Número de AGENTES com conversas que ultrapassaram o tempo de SLA. SLA = ${slaMinutes} minutos (tempo máximo para primeira resposta). Conversas não respondidas nesse tempo são consideradas 'fora do SLA'.`} />
              </span>
            </div>
            <p className={`text-3xl font-bold ${slaStatus.agents_breaching_sla > 0 ? 'text-red-600' : 'text-gray-500'}`}>{summary.agents_breaching_sla || 0}</p>
          </div>
          <div className={`bg-white rounded-xl shadow-sm border-l-4 ${performanceStatus.underperforming_count > 0 ? 'border-yellow-500' : 'border-gray-300'} p-4 hover:shadow-md transition-shadow`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 ${performanceStatus.underperforming_count > 0 ? 'bg-yellow-100' : 'bg-gray-100'} rounded-lg`}>
                <ChartBarIcon className={`w-5 h-5 ${performanceStatus.underperforming_count > 0 ? 'text-yellow-600' : 'text-gray-500'}`} />
              </div>
              <span className={`text-sm font-medium ${performanceStatus.underperforming_count > 0 ? 'text-yellow-700' : 'text-gray-600'} inline-flex items-center`}>
                Baixa Performance
                <InfoTooltip text="Agentes que estiveram online hoje mas com baixa performance. Considera: conversas criadas + reabertas + interações em existentes. Apenas agentes online ou com atividade no dia" />
              </span>
            </div>
            <p className={`text-3xl font-bold ${performanceStatus.underperforming_count > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>{summary.underperforming_agents || 0}</p>
          </div>
          <div className={`bg-white rounded-xl shadow-sm border-l-4 ${actionAlerts.summary?.total_alerts > 0 ? 'border-primary-500' : 'border-gray-300'} p-4 hover:shadow-md transition-shadow`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 ${actionAlerts.summary?.total_alerts > 0 ? 'bg-primary-100' : 'bg-gray-100'} rounded-lg`}>
                <BellAlertIcon className={`w-5 h-5 ${actionAlerts.summary?.total_alerts > 0 ? 'text-primary-600' : 'text-gray-500'}`} />
              </div>
              <span className={`text-sm font-medium ${actionAlerts.summary?.total_alerts > 0 ? 'text-primary-700' : 'text-gray-600'} inline-flex items-center`}>
                Ações Urgentes
                <InfoTooltip text={`Total de alertas críticos que precisam de ação imediata. Soma de: conversas sem resposta urgentes (>${slaMinutes}min) + conversas sem agente + reabertas hoje`} />
              </span>
            </div>
            <p className={`text-3xl font-bold ${actionAlerts.summary?.total_alerts > 0 ? 'text-primary-600' : 'text-gray-500'}`}>{summary.urgent_actions_needed || 0}</p>
          </div>
        </div>

        {/* KPIs em Tempo Real */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ClockIcon className="w-5 h-5 text-primary-600" />
            </div>
            KPIs em Tempo Real
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center bg-gradient-to-br from-primary-50 to-white rounded-xl p-4 border border-primary-100">
              <p className="text-gray-600 text-sm font-medium inline-flex items-center justify-center gap-1">
                Ativos por Operador
                <InfoTooltip text="Média de conversas ativas por agente agora. Calculado: Total de conversas ativas ÷ Total de agentes online" />
              </p>
              <p className="text-2xl font-bold text-primary-600 mt-1">{realTimeKPIs?.avg_active_per_agent || 0}</p>
            </div>
            <div className="text-center bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
              <p className="text-gray-600 text-sm font-medium inline-flex items-center justify-center gap-1">
                Total Ativos
                <InfoTooltip text="Total de conversas em atendimento AGORA. Status = Aberto OU Pendente" />
              </p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{realTimeKPIs?.total_active || 0}</p>
            </div>
            <div className="text-center bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
              <p className="text-gray-600 text-sm font-medium inline-flex items-center justify-center gap-1">
                SLA Atual (24h)
                <InfoTooltip text="Percentual de conversas respondidas no prazo nas últimas 24h. Verde ≥80%, Amarelo ≥60%, Vermelho <60%" />
              </p>
              <p className={`text-2xl font-bold mt-1 ${(realTimeKPIs?.current_sla_percentage || 0) >= 80 ? 'text-green-600' : (realTimeKPIs?.current_sla_percentage || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                {realTimeKPIs?.current_sla_percentage || 0}%
              </p>
            </div>
            <div className="text-center bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
              <p className="text-gray-600 text-sm font-medium inline-flex items-center justify-center gap-1">
                Fila Atual
                <InfoTooltip text="Total de conversas aguardando primeira resposta AGORA. Vermelho se >20" />
              </p>
              <p className={`text-2xl font-bold mt-1 ${(realTimeKPIs?.current_queue || 0) > 20 ? 'text-red-600' : 'text-gray-800'}`}>
                {realTimeKPIs?.current_queue || 0}
              </p>
            </div>
            <div className="text-center bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-100">
              <p className="text-gray-600 text-sm font-medium inline-flex items-center justify-center gap-1">
                TMA Hoje
                <InfoTooltip text="Tempo Médio de Atendimento das conversas fechadas hoje. Média de (última atividade - criação)" />
              </p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{formatMinutes(realTimeKPIs?.current_tma_minutes)}</p>
            </div>
          </div>
        </div>

        {/* Onde preciso agir agora? */}
        <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
            <div className="p-2 bg-primary-100 rounded-lg">
              <BellAlertIcon className="w-5 h-5 text-primary-600" />
            </div>
            <span className="inline-flex items-center gap-1">
              Onde preciso agir agora?
              <InfoTooltip text="Alertas críticos que precisam de ação imediata: conversas sem resposta urgentes + conversas sem agente + reabertas hoje. Todas as conversas são do dia atual" />
            </span>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4">
              <p className="text-red-700 text-sm font-medium inline-flex items-center gap-1">
                Sem resposta ({'>'} {slaMinutes}min)
                <InfoTooltip text={`Número de CONVERSAS onde o cliente está aguardando resposta há mais de ${slaMinutes} minutos. Cliente enviou última mensagem e agente não respondeu`} />
              </p>
              <p className="text-3xl font-bold text-red-600 mt-1">{actionAlerts.summary?.urgent_no_reply || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-white border border-yellow-200 rounded-xl p-4">
              <p className="text-yellow-700 text-sm font-medium inline-flex items-center gap-1">
                Sem agente atribuído
                <InfoTooltip text="Número de CONVERSAS que não têm nenhum agente designado para atender" />
              </p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{actionAlerts.summary?.unassigned || 0}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl p-4">
              <p className="text-purple-700 text-sm font-medium inline-flex items-center gap-1">
                Reabertas hoje
                <InfoTooltip text={`Conversas reabertas hoje e sem resposta há mais de ${slaMinutes} minutos. Cliente voltou a entrar em contato e aguarda retorno`} />
              </p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{actionAlerts.summary?.reopened || 0}</p>
            </div>
          </div>

          {/* Lista de conversas urgentes */}
          {actionAlerts.urgent_conversations?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-red-600 mb-2">
                Conversas sem resposta urgentes ({actionAlerts.urgent_conversations.length})
              </h3>
              <div className="max-h-96 overflow-y-auto space-y-1">
                {actionAlerts.urgent_conversations.map((conv) => {
                  const teams = Array.isArray(conv.agent_teams) ? conv.agent_teams : [];
                  return (
                    <div key={conv.conversation_id} className="flex justify-between items-center p-2 bg-red-50 rounded-lg text-sm hover:bg-red-100 transition-colors">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PhoneIcon className="w-4 h-4 text-gray-400" />
                        <a
                          href={getConversationUrl(conv.display_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
                        >
                          #{conv.display_id}
                        </a>
                        <span className="text-gray-500">- {conv.contact_name || conv.contact_phone || 'Sem nome'}</span>
                        {teams.length > 0 && (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                            {teams.map(t => t.name).join(', ')}
                          </span>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap ml-2">
                        <span className="text-red-600 font-medium">{formatMinutes(conv.wait_minutes)}</span>
                        <span className="text-gray-500 ml-2">{conv.agent_name || 'Sem agente'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Lista de conversas sem agente */}
          {actionAlerts.unassigned_conversations?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-yellow-600 mb-2">
                Conversas sem agente ({actionAlerts.unassigned_conversations.length})
              </h3>
              <div className="max-h-96 overflow-y-auto space-y-1">
                {actionAlerts.unassigned_conversations.map((conv) => (
                  <div key={conv.conversation_id} className="flex justify-between items-center p-2 bg-yellow-50 rounded-lg text-sm hover:bg-yellow-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <XCircleIcon className="w-4 h-4 text-yellow-500" />
                      <a
                        href={getConversationUrl(conv.display_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
                      >
                        #{conv.display_id}
                      </a>
                      <span className="text-gray-500">- {conv.contact_name || conv.contact_phone || 'Sem nome'}</span>
                    </div>
                    <div className="text-right whitespace-nowrap ml-2">
                      <span className="text-yellow-600 font-medium">{formatMinutes(conv.wait_minutes)}</span>
                      <span className="text-gray-500 ml-2">{conv.inbox_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lista de conversas reabertas */}
          {actionAlerts.reopened_conversations?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-purple-600 mb-2">
                Conversas reabertas hoje ({actionAlerts.reopened_conversations.length})
              </h3>
              <div className="max-h-96 overflow-y-auto space-y-1">
                {actionAlerts.reopened_conversations.map((conv) => {
                  const teams = Array.isArray(conv.agent_teams) ? conv.agent_teams : [];
                  return (
                    <div key={conv.conversation_id} className="flex justify-between items-center p-2 bg-purple-50 rounded-lg text-sm hover:bg-purple-100 transition-colors">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ArrowPathIcon className="w-4 h-4 text-purple-500" />
                        <a
                          href={getConversationUrl(conv.display_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
                        >
                          #{conv.display_id}
                        </a>
                        <span className="text-gray-500">- {conv.contact_name || conv.contact_phone || 'Sem nome'}</span>
                        {teams.length > 0 && (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                            {teams.map(t => t.name).join(', ')}
                          </span>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap ml-2">
                        <span className="text-purple-600 font-medium">{formatMinutes(conv.wait_minutes)}</span>
                        <span className="text-gray-500 ml-2">{conv.agent_name || 'Sem agente'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* Produtividade dos Agentes */}
        <AgentProductivityCards
          data={agentProductivity}
          loading={loading && !agentProductivity}
        />

        {/* Conversas Pendentes de Retorno */}
        <PendingConversationsTable
          conversations={pendingConversations?.conversations || []}
          pagination={pendingConversations?.pagination || {}}
          loading={loading && !pendingConversations}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          sortField={pendingSortField}
          sortDirection={pendingSortDirection}
          filters={filters}
        />

        {/* Seções existentes do dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 1. Quem está logado agora? */}
          <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserIcon className="w-5 h-5 text-green-600" />
              </div>
              <span className="inline-flex items-center gap-1">
                1. Quem está logado agora?
                <InfoTooltip text="Lista de agentes e seu status de disponibilidade atual (Online, Ocupado, Offline) com número de conversas ativas e pendentes sem resposta" />
              </span>
            </h2>
            <div className="flex gap-4 mb-4 text-sm">
              <span className="text-green-600">Online: {onlineStatus.online || 0}</span>
              <span className="text-yellow-600">Ocupado: {onlineStatus.busy || 0}</span>
              <span className="text-gray-500">Offline: {onlineStatus.offline || 0}</span>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {onlineStatus.agents?.map((agent) => (
                <div
                  key={agent.agent_id}
                  className={`flex justify-between items-center p-2 rounded ${
                    parseInt(agent.availability) === 0 ? 'bg-green-50' :
                    parseInt(agent.availability) === 2 ? 'bg-yellow-50' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      parseInt(agent.availability) === 0 ? 'bg-green-500' :
                      parseInt(agent.availability) === 2 ? 'bg-yellow-500' : 'bg-gray-400'
                    }`} />
                    <span className="text-sm text-gray-900">{agent.agent_name}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {agent.active_conversations} ativos | {agent.pending_first_reply} s/ resp
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Quem está com fila alta? */}
          <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
              <div className="p-2 bg-orange-100 rounded-lg">
                <UserGroupIcon className="w-5 h-5 text-orange-600" />
              </div>
              <span className="inline-flex items-center gap-1">
                2. Quem está com fila alta?
                <InfoTooltip text="Agentes com muitas conversas na fila (≥10 = crítico, ≥5 = atenção). Mostra total de conversas abertas+pendentes, não lidas e tempo médio de espera" />
              </span>
            </h2>
            <p className="text-sm text-gray-500 mb-4 inline-flex items-center gap-2">
              Total na fila: {queueStatus.total_in_queue || 0} | Agentes críticos: {queueStatus.agents_with_high_queue || 0}
              <InfoTooltip text="'Total na fila' = soma de TODAS as conversas. 'Agentes críticos' = número de AGENTES com ≥10 conversas" />
            </p>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {queueStatus.agents?.filter(a => parseInt(a.total_queue) > 0).map((agent) => (
                <div
                  key={agent.agent_id}
                  className={`flex justify-between items-center p-2 rounded border ${
                    agent.queue_status === 'critical' ? 'bg-red-50 border-red-300' :
                    agent.queue_status === 'warning' ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div>
                    <span className="text-sm text-gray-900">{agent.agent_name}</span>
                    <div className="text-xs text-gray-500">
                      Abertos: {agent.open_conversations} | Pendentes: {agent.pending_conversations}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-lg font-bold ${
                      agent.queue_status === 'critical' ? 'text-red-600' :
                      agent.queue_status === 'warning' ? 'text-yellow-600' : 'text-gray-900'
                    }`}>
                      {agent.total_queue}
                    </span>
                    <div className="text-xs text-gray-500">na fila</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Quem está estourando SLA? */}
          <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
              </div>
              <span className="inline-flex items-center gap-1">
                3. Quem está estourando SLA?
                <InfoTooltip text={`SLA = Service Level Agreement (tempo máximo para primeira resposta). Mostra agentes com conversas que ultrapassaram ${slaMinutes} minutos sem resposta ou foram respondidas fora do prazo`} />
              </span>
            </h2>
            <p className="text-sm text-gray-500 mb-4 inline-flex items-center gap-2">
              Limite SLA: {slaMinutes} min | Conversas estouradas: {slaStatus.total_breached_conversations || 0}
              <InfoTooltip text="'Pendentes' = ainda não respondidas. 'Atrasados' = respondidas mas fora do prazo" />
            </p>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {slaStatus.agents?.filter(a => parseInt(a.total_breached) > 0).map((agent) => (
                <div key={agent.agent_id}>
                  <div
                    className="flex justify-between items-center p-2 rounded bg-red-50 border border-red-300 cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => setExpandedSlaAgents(prev => ({ ...prev, [agent.agent_id]: !prev[agent.agent_id] }))}
                  >
                    <div className="flex items-center gap-2">
                      {expandedSlaAgents[agent.agent_id] ? (
                        <ChevronUpIcon className="w-4 h-4 text-red-500" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-red-500" />
                      )}
                      <div>
                        <span className="text-sm text-gray-900">{agent.agent_name}</span>
                        <div className="text-xs text-gray-500">
                          Pendentes: {agent.sla_breached_pending} | Atrasados: {agent.sla_breached_responded}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-red-600">{agent.total_breached}</span>
                      <div className="text-xs text-gray-500">estourados</div>
                    </div>
                  </div>
                  {expandedSlaAgents[agent.agent_id] && agent.conversations?.length > 0 && (
                    <div className="ml-6 mt-1 space-y-1 mb-2">
                      {agent.conversations.map((conv) => {
                        const teams = Array.isArray(conv.agent_teams) ? conv.agent_teams : [];
                        return (
                          <div key={conv.conversation_id} className="flex justify-between items-center p-2 bg-white rounded border border-red-200 text-sm">
                            <div className="flex items-center gap-2 flex-wrap">
                              <a
                                href={getConversationUrl(conv.display_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
                              >
                                #{conv.display_id}
                              </a>
                              <span className="text-gray-500">{conv.contact_name || conv.contact_phone || 'Sem nome'}</span>
                              <span className={`px-1.5 py-0.5 text-xs rounded ${conv.breach_type === 'pending' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {conv.breach_type === 'pending' ? 'Sem resposta' : 'Respondido tarde'}
                              </span>
                              {teams.length > 0 && (
                                <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                                  {teams.map(t => t.name).join(', ')}
                                </span>
                              )}
                            </div>
                            <span className="text-red-600 font-medium whitespace-nowrap ml-2">{formatMinutes(conv.wait_minutes)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {(!slaStatus.agents || slaStatus.agents.filter(a => parseInt(a.total_breached) > 0).length === 0) && (
                <div className="text-center text-green-600 py-4">
                  <CheckCircleIcon className="w-8 h-8 mx-auto mb-2" />
                  Nenhum agente estourando SLA
                </div>
              )}
            </div>
          </div>

          {/* 4. Quem não está performando hoje? */}
          <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <ChartBarIcon className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="inline-flex items-center gap-1">
                4. Quem não está performando hoje?
                <InfoTooltip text="Mostra apenas agentes que estão online agora ou tiveram atividade hoje (enviaram mensagens). Critérios de baixa performance: poucas interações (criadas + reabertas + atividades < 10) OU muitos pendentes (>50%) OU resposta lenta (>10min)" />
              </span>
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              {performanceStatus.date} | Agentes ativos: {performanceStatus.total_agents || 0} | Baixa performance: {performanceStatus.underperforming_count || 0}
            </p>

            {/* Legenda das métricas */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3 pb-3 border-b">
              <span title="Conversas criadas/recebidas hoje">Criadas</span>
              <span title="Conversas reabertas hoje">Reabertas</span>
              <span title="Conversas com interação (mensagens enviadas) hoje">Interações</span>
              <span title="Conversas resolvidas hoje">Resolvidas</span>
            </div>

            <div className="max-h-80 overflow-y-auto space-y-2">
              {performanceStatus.agents?.filter(a => a.is_underperforming).map((agent) => {
                const availLabel = parseInt(agent.availability) === 0 ? 'Online' : parseInt(agent.availability) === 2 ? 'Ocupado' : 'Offline';
                const availColor = parseInt(agent.availability) === 0 ? 'bg-green-500' : parseInt(agent.availability) === 2 ? 'bg-yellow-500' : 'bg-gray-400';
                return (
                  <div
                    key={agent.agent_id}
                    className={`p-3 rounded-lg border ${
                      agent.performance_status === 'no_activity' ? 'bg-red-50 border-red-300' :
                      agent.performance_status === 'low_activity' ? 'bg-yellow-50 border-yellow-300' :
                      agent.performance_status === 'high_pending' ? 'bg-orange-50 border-orange-300' :
                      agent.performance_status === 'slow_response' ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <div className={`w-2 h-2 rounded-full ${availColor}`} />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">{agent.agent_name}</span>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                              agent.performance_status === 'no_activity' ? 'bg-red-100 text-red-700' :
                              agent.performance_status === 'low_activity' ? 'bg-yellow-100 text-yellow-700' :
                              agent.performance_status === 'high_pending' ? 'bg-orange-100 text-orange-700' :
                              agent.performance_status === 'slow_response' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {agent.performance_status === 'no_activity' && 'Sem atividade'}
                              {agent.performance_status === 'low_activity' && 'Baixa atividade'}
                              {agent.performance_status === 'high_pending' && 'Muitos pendentes'}
                              {agent.performance_status === 'slow_response' && 'Resposta lenta'}
                            </span>
                            <span className="text-xs text-gray-400">{availLabel}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-2 text-xs">
                      <div className="flex flex-col items-center px-2 py-1 bg-white/60 rounded">
                        <span className="font-semibold text-gray-800">{agent.conversations_today || 0}</span>
                        <span className="text-gray-500">Criadas</span>
                      </div>
                      <div className="flex flex-col items-center px-2 py-1 bg-white/60 rounded">
                        <span className="font-semibold text-gray-800">{agent.reopened_today || 0}</span>
                        <span className="text-gray-500">Reabertas</span>
                      </div>
                      <div className="flex flex-col items-center px-2 py-1 bg-white/60 rounded">
                        <span className="font-semibold text-gray-800">{agent.activities_today || 0}</span>
                        <span className="text-gray-500">Interações</span>
                      </div>
                      <div className="flex flex-col items-center px-2 py-1 bg-white/60 rounded">
                        <span className="font-semibold text-gray-800">{agent.resolved_today || 0}</span>
                        <span className="text-gray-500">Resolvidas</span>
                      </div>
                      {agent.avg_response_time_today && (
                        <div className="flex flex-col items-center px-2 py-1 bg-white/60 rounded">
                          <span className={`font-semibold ${parseFloat(agent.avg_response_time_today) > 10 ? 'text-red-600' : 'text-gray-800'}`}>
                            {formatMinutes(parseFloat(agent.avg_response_time_today))}
                          </span>
                          <span className="text-gray-500">TMR</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {(!performanceStatus.agents || performanceStatus.agents.filter(a => a.is_underperforming).length === 0) && (
                <div className="text-center text-green-600 py-4">
                  <CheckCircleIcon className="w-8 h-8 mx-auto mb-2" />
                  Todos performando bem
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rodapé */}
        <div className="text-center text-sm text-gray-500 py-4 border-t border-gray-200 mt-4">
          <span className="font-medium text-primary-600">Dashboard do Supervisor</span> | Atualização automática: {autoRefresh ? <span className="text-green-600">Ativa (30s)</span> : <span className="text-gray-400">Desativada</span>}
        </div>
      </div>
    </div>
  );
};

export default SupervisorDashboard;
